import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { transactionService } from '../services/transactionService';
import { isZeroPricedCartItem, itemLabel } from '../contexts/CartContext';

/**
 * Hook do zarzadzania szkicami (drafts) w POS
 * Zapisuje i laduje szkice z localStorage
 */
const useDrafts = ({
  cart,
  setCart,
  selectedCustomer,
  setSelectedCustomer,
  currentShift,
  currentLocationId,
  getFinalTotal,
  clearCart,
  setActiveTab,
  setLoading,
  setError
}) => {
  /**
   * Zapisanie aktualnego koszyka jako szkic
   */
  const saveDraft = useCallback(async () => {
    if (cart.length === 0) {
      setError("Nie mozna zapisac pustego koszyka");
      return;
    }

    if (!currentLocationId) {
      setError("Najpierw wybierz lokalizacje w prawym gornym rogu!");
      return;
    }

    if (!currentShift) {
      setError("Brak otwartej zmiany! Najpierw otworz zmiane kasowa.");
      return;
    }

    setLoading(true);
    try {
      const draftData = {
        customer: selectedCustomer,
        items: cart,
        total: getFinalTotal(),
        created_at: new Date().toISOString(),
      };

      localStorage.setItem(`draft_${Date.now()}`, JSON.stringify(draftData));

      clearCart();
      toast.success("Szkic zostal zapisany pomyslnie!");
    } catch (err) {
      setError("Blad zapisywania szkicu");
    } finally {
      setLoading(false);
    }
  }, [cart, selectedCustomer, currentShift, currentLocationId, getFinalTotal, clearCart, setLoading, setError]);

  /**
   * Zaladowanie szkicu do koszyka
   */
  const loadDraftToPOS = useCallback(async (draft) => {
    if (draft.items && draft.items.length > 0) {
      // Pozycje bez ceny (dokladnie 0.00) nie wchodza z powrotem do koszyka -
      // decyzja biznesowa wlasciciela (2026-08): towar z cena 0.00 nie wchodzi
      // do koszyka, minimum 0.01 zl. Protokol XML (pxml.txt) NIE stawia
      // warunku > 0 - to nie wymog drukarki. Kaucje (takze 0.00) przepuszczamy.
      const zeroPriced = draft.items.filter(isZeroPricedCartItem);
      const items = draft.items.filter(item => !isZeroPricedCartItem(item));

      if (zeroPriced.length > 0) {
        toast.error(
          `Pominieto pozycje bez ceny (0,00 zl): ${zeroPriced.map(itemLabel).join(', ')}. ` +
          'Uzupelnij cennik i dodaj je ponownie.'
        );
      }

      // Szkic zlozony WYLACZNIE z pozycji bez ceny zostaje nietkniety - inaczej
      // skasowalibysmy go ponizej i kasjerka stracilaby liste towarow.
      if (items.length === 0) {
        return;
      }

      setCart(items);
      if (draft.customer) {
        setSelectedCustomer(draft.customer);
      }

      // Usun/oznacz szkic jako zuzyty po zaladowaniu, aby uniknac podwojnej sprzedazy.
      // Szkic z bazy (source === 'database') trzeba skasowac przez API - sam localStorage
      // nic tam nie usuwa, wiec szkic zostawal i mozna go bylo zaladowac ponownie.
      try {
        if (draft.source === 'database' && draft.id != null) {
          await transactionService.deleteTransaction(draft.id);
        } else if (draft.key) {
          localStorage.removeItem(draft.key);
        }
      } catch (err) {
        // Nie blokuj zaladowania koszyka, ale zaloguj problem z usunieciem szkicu
        console.error('Nie udalo sie usunac szkicu po zaladowaniu:', err);
      }

      // Przelacz na zakladke POS
      setActiveTab("pos");

      toast.success("Szkic zostal zaladowany do koszyka!");
    }
  }, [setCart, setSelectedCustomer, setActiveTab]);

  /**
   * Pobranie listy szkicow z localStorage
   */
  const getDrafts = useCallback(() => {
    const drafts = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('draft_')) {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          drafts.push({ ...data, key });
        } catch (e) {
          // draft parse error
        }
      }
    }
    return drafts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, []);

  /**
   * Usuniecie szkicu
   */
  const deleteDraft = useCallback((draftKey) => {
    localStorage.removeItem(draftKey);
  }, []);

  return {
    saveDraft,
    loadDraftToPOS,
    getDrafts,
    deleteDraft
  };
};

export default useDrafts;
